import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { useImportOnboarding } from '../../contexts/ImportOnboardingContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { newestMonthHint, isPartialImport } from '../../modules/ImportOnboarding/utils/importDerivedState';

type Props = {
  /** Optional override — defaults to reopening the import sheet. */
  onPress?: () => void;
};

/**
 * Compact background-import indicator for Home / Spend.
 * Hidden while the import sheet is open; tap reopens live progress.
 */
export function ImportProgressChip({ onPress }: Props) {
  const { isModalVisible, openImportMore, progress, busy } = useImportOnboarding();

  const visible =
    !isModalVisible &&
    (busy || progress.phase === 'complete' || progress.phase === 'failed');

  if (!visible) return null;

  const monthHint = newestMonthHint(progress.transactions);
  const fileErrors = progress.fileErrors ?? [];
  const partial = isPartialImport(progress);

  const analyzed =
    progress.phase === 'uploading'
      ? `Uploading ${progress.uploadIndex}/${progress.uploadTotal || progress.totalImages || '?'}`
      : progress.totalImages > 0
        ? `Analyzing ${Math.min(progress.processedImages, progress.totalImages)}/${progress.totalImages}`
        : 'Analyzing…';

  const txPart =
    progress.transactionCount > 0
      ? ` · ${progress.transactionCount} tx${progress.transactionCount === 1 ? '' : 's'}`
      : '';

  const label =
    progress.phase === 'complete'
      ? partial
        ? progress.statusMessage ||
          `${progress.processedImages}/${progress.totalImages} analyzed · ${fileErrors.length} failed`
        : progress.transactionCount > 0
          ? `Import done · ${progress.transactionCount} txs`
          : 'Import finished'
      : progress.phase === 'failed'
        ? progress.error || 'Import failed'
        : fileErrors.length > 0
          ? progress.statusMessage || analyzed
          : `${analyzed}${txPart}${monthHint ? ` · ${monthHint}` : ''}`;

  const handlePress = () => {
    if (onPress) onPress();
    else openImportMore();
  };

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={`${label}. Tap to open import progress.`}
      style={({ pressed }) => [styles.pressable, pressed && styles.pressed]}
    >
      <View
        style={[
          styles.chip,
          progress.phase === 'failed' && styles.chipFailed,
          partial && progress.phase !== 'failed' && styles.chipWarn,
        ]}
      >
        {busy ? <ActivityIndicator color={colors.accent} size="small" /> : null}
        <View style={styles.copy}>
          <Text
            style={[
              styles.label,
              progress.phase === 'complete' && !partial && styles.labelOk,
              (progress.phase === 'complete' && partial) || fileErrors.length > 0
                ? styles.labelWarn
                : null,
              progress.phase === 'failed' && styles.labelError,
            ]}
            numberOfLines={2}
          >
            {label}
          </Text>
          <Text style={styles.hint}>Tap to open</Text>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressable: {
    alignSelf: 'stretch',
  },
  pressed: {
    opacity: 0.88,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    borderRadius: radii.lg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
    minHeight: 44,
  },
  chipWarn: {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    borderColor: 'rgba(245, 158, 11, 0.28)',
  },
  chipFailed: {
    backgroundColor: 'rgba(248, 113, 113, 0.12)',
    borderColor: 'rgba(248, 113, 113, 0.28)',
  },
  copy: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  label: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    fontSize: 14,
  },
  labelOk: {
    color: colors.accent,
  },
  labelWarn: {
    color: colors.warning,
  },
  labelError: {
    color: colors.danger,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
});
